import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import ManageStockModal from "../../../components/ManageStockModal";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useModalStore from "../../../stores/useModalStore";
import useDialogStore from "../../../stores/useDialogStore";
import ReserveMultipleDevices from "../../../components/ReserveMultipleDevices";
import CreateInvoiceByDeviceId from "../../../components/CreateInvoiceByDeviceId";
import { toast } from "react-toastify";
import {
  XMarkIcon,
  ArrowPathIcon,
  SwatchIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import ColumnSearch from "../../../components/ColumnSearch";
import EditableCell from "../../../components/EditableCell";
import EditableSelectCell from "../../../components/EditableSelectCell";
import EditableDateCell from "../../../components/EditableDateCell";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import dayjs from "dayjs";
import excelIcon from "../../../assets/excel-icon.webp";
import ColumnSelector from "../../../components/ColumnSelector";
import ActionButtons from "../../../components/ActionButtons";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

export default function ReserveStock() {
  const { showModal, setShowModal } = useModalStore();
  const [activeDevice, setActiveDevice] = useState(null);
  const { openDialog, closeDialog } = useDialogStore();
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const sortConfig = {
    key: null,
    direction: "desc",
  };
  const [selectedColumns, setSelectedColumns] = useState({
    unique_id: true,
    brand: true,
    model: true,
    serial_no: true,
    counter_black: true,
    counter_color: true,
    total_counter: true,
    palette: true,
    created_at: true,
    options: true,
    comment: true,
  });

  const columnOptions = [
    { key: "unique_id", label: "Unique ID" },
    { key: "brand", label: "Brand" },
    { key: "model", label: "Model" },
    { key: "serial_no", label: "Serial No" },
    { key: "counter_black", label: "Counter Black" },
    { key: "counter_color", label: "Counter Color" },
    { key: "total_counter", label: "Total Counter" },
    { key: "palette", label: "Palette" },
    { key: "created_at", label: "Date Registered" },
    { key: "comment", label: "Comment" },
    { key: "options", label: "Device Options" },
  ];

  const ColumnSelectorContent = ({ onExport }) => {
    const [localSelectedColumns, setLocalSelectedColumns] =
      useState(selectedColumns);

    return (
      <div className="bg-white rounded-lg w-full p-4">
        <ColumnSelector
          columns={columnOptions}
          selectedColumns={localSelectedColumns}
          setSelectedColumns={setLocalSelectedColumns}
        />
        <div className="mt-6 flex justify-end space-x-2">
          <Button variant="light" size="sm" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setSelectedColumns(localSelectedColumns);
              onExport(localSelectedColumns);
            }}
          >
            {exportMutation?.isPending ? (
              <div className="flex items-center gap-2">
                <div className="loader h-4 w-4 border-[2px] border-t-transparent border-white rounded-full animate-spin"></div>
                Loading...
              </div>
            ) : (
              "Export"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const handleOpenColumnSelector = () => {
    openDialog({
      title: "Select Columns to Export",
      content: (
        <ColumnSelectorContent
          onExport={(finalColumns) => {
            handleExport(finalColumns);
          }}
        />
      ),
    });
  };

  const exportMutation = useMutation({
    mutationFn: async (selectedColumnss) => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/export-excel/non-status-devices`,
        {
          columns: Object.entries(selectedColumnss)
            .filter(([_, selected]) => selected)
            .map(([key]) => key),
          params: params.toString(),
        },
        {
          responseType: "blob", // This tells Axios to expect a file
        }
      );
      return response;
    },
    onSuccess: (response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `reserve_stock_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up

      closeDialog();
      toast.success("Report exported successfully!");
    },
    onError: (error) => {
      console.error("Export error:", error);
      toast.error("Failed to export report. Please try again.");
    },
  });

  const handleExport = async (selectedColumnss) => {
    if (Object.values(selectedColumnss).some((selected) => selected)) {
      exportMutation.mutate(selectedColumnss);
    } else {
      toast.warning("Please select at least one column to export");
    }
  };

  // Fetch devices using React Query
  const {
    data: devicesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["not-reserved-devices", location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      const response = await axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT
        }/devices/status/not-reserved?${params.toString()}`
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    cacheTime: 0,
  });
  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(["not-reserved-devices"]);
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["not-reserved-devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    navigate(`${location.pathname}?${params.toString()}`);
    queryClient.invalidateQueries(["not-reserved-devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    queryClient.invalidateQueries(["not-reserved-devices"]);
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) =>
      data.data.data.map(({ id, name }) => ({ id: id.toString(), name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id: id.toString(),
        name,
        device_brand_id,
      })),
  }).data;

  const openMultipleReserveModal = () => {
    openDialog({
      title: "Reserve Multiple Devices by Unique ID",
      content: <ReserveMultipleDevices />,
    });
  };

  const openCreateInvoiceByDeviceIdModal = () => {
    openDialog({
      title: "Create Invoice by Device ID",
      content: <CreateInvoiceByDeviceId />,
    });
  };

  const devices = devicesData?.data || [];
  const paginationData = {
    current_page: devicesData?.current_page || 1,
    last_page: devicesData?.last_page || 1,
    next_page_url: devicesData?.next_page_url,
    prev_page_url: devicesData?.prev_page_url,
  };

  const deleteDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.delete(`${window.REACT_APP_API_ENDPOINT}/devices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(["recycledDevices"]);
      toast.success("Device deleted successfully!");
    },
    onError: (error) => {
      console.error("Delete device error:", error);
    },
  });
  const handleDelete = (id) => {
    openDialog({
      title: "Delete Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to delete this device?
            <br />
            <span className="text-red-500 text-sm font-bold">
              (It will be moved to the trash bin)
            </span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                deleteDeviceMutation.mutate(id);
                closeDialog();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Reserve Stock
                </h4>
                <div className="flex items-center lg:flex-row flex-col gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<XMarkIcon className="h-4 w-4" />}
                    onClick={handleClearFilters}
                    className="mb-2 lg:mb-0"
                  >
                    Clear Filters
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    icon={excelIcon}
                    onClick={handleOpenColumnSelector}
                    className="mb-2 lg:mb-0"
                  >
                    Export Reserve Stock Data as CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<SwatchIcon className="h-4 w-4" />}
                    onClick={openMultipleReserveModal}
                    className="mb-2 lg:mb-0"
                  >
                    + Reserve Multiple Devices by Unique ID
                  </Button>
                  {/* <Button
                    variant="outline"
                    size="sm"
                    icon={<SwatchIcon className="h-4 w-4" />}
                    onClick={openCreateInvoiceByDeviceIdModal}
                    className="mb-2 lg:mb-0"
                  >
                    + Create Invoice by Device ID
                  </Button> */}
                  <Button
                    href="/admin/stock/reserved"
                    variant="primary"
                    size="sm"
                    icon={ArrowPathIcon}
                  >
                    Return to Reserved Stocks
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Unique ID"
                            columnKey="unique_id"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Unique ID..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Brand"
                            columnKey="brand"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Brand..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Model"
                            columnKey="model"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Model..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Serial No"
                            columnKey="serial_no"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Serial No..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Black"
                            columnKey="counter_black"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Black..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Counter Color"
                            columnKey="counter_color"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Counter Color..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Total Counter"
                            columnKey="total_counter"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Total Counter..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Palette"
                            columnKey="palette"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            placeholder="Search by Palette..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        <div className="flex items-center gap-2">
                          <ColumnSearch
                            columnName="Date Registered"
                            columnKey="created_at"
                            onSearch={handleColumnSearch}
                            onSort={handleColumnSort}
                            sortConfig={sortConfig}
                            isDate={true}
                            placeholder="Search by Date..."
                          />
                        </div>
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Options
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm">
                        Comment
                      </th>
                      <th className="px-4 py-4 text-left text-white text-sm min-w-[200px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : devices.length == 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center">
                          <NoData message="No devices found" />
                        </td>
                      </tr>
                    ) : (
                      devices.map((device, index) => (
                        <tr
                          key={device.id}
                          className={`divide-y divide-gray-200 ${index % 2 === 0 ? "bg-white" : "bg-gray-100"
                            }`}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {device.unique_id}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {brands && device.device_brand && (
                              <EditableSelectCell
                                value={device.device_brand_id?.toString()}
                                displayValue={device.device_brand?.name || ""}
                                id={device.id}
                                options={brands}
                                field="brand"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "device_brand_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {models && device.device_model && (
                              <EditableSelectCell
                                value={device.device_model?.id?.toString()}
                                displayValue={device.device_model?.name || ""}
                                id={device.id}
                                options={models.filter(
                                  (model) =>
                                    model.device_brand_id ===
                                    device.device_brand_id
                                )}
                                field="model"
                                onSave={(data) =>
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "device_model_id",
                                    value: data.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditSerialNumberCell
                              value={device.serial_no}
                              displayValue={device.serial_no}
                              id={device.id}
                              field="serial_no"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "serial_no",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={device.counter_black}
                              displayValue={device.counter_black || "---"}
                              id={device.id}
                              field="counter_black"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_black",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={device.counter_color}
                              displayValue={device.counter_color || "---"}
                              id={device.id}
                              field="counter_color"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "counter_color",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={device.total_counter}
                              displayValue={device.total_counter || "---"}
                              id={device.id}
                              field="total_counter"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "total_counter",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={device.palette}
                              displayValue={device.palette || "---"}
                              id={device.id}
                              field="palette"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "palette",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableDateCell
                              value={device.created_at}
                              displayValue={
                                device.created_at
                                  ? dayjs
                                    .utc(device.created_at)
                                    .format("DD/MM/YYYY")
                                  : "---"
                              }
                              id={device.id}
                              field="created_at"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "created_at",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableOptionsCell
                              deviceId={device.id}
                              deviceOptions={device.device_options || {}}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={device.comment}
                              displayValue={device.comment || "---"}
                              id={device.id}
                              field="comment"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "comment",
                                  value: data.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-4 py-3 text-gray-900 flex  items-center">
                            <Button
                              onClick={() => {
                                setShowModal(true);
                                setActiveDevice(device);
                              }}
                              className="h-8 inline-flex items-center text-sm font-bold px-4 py-2 whitespace-nowrap"
                              variant="outline"
                              color="bluePrimary"
                            >
                              + Reserve Stock
                            </Button>

                            <ActionButtons
                              id={device.id}
                              onDelete={() => handleDelete(device.id)}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationV2
                current_page={paginationData.current_page}
                last_page={paginationData.last_page}
                next_page_url={paginationData.next_page_url}
                prev_page_url={paginationData.prev_page_url}
              />
            </div>
          </div>
        </div>
        {showModal && (
          <ManageStockModal
            device={activeDevice}
            id={activeDevice.id}
            status={"reserved"}
            onSuccess={() => {
              queryClient.invalidateQueries(["not-reserved-devices"]);
              setShowModal(false);
            }}
          />
        )}
      </div>
    </AdminSidebar>
  );
}
